export type PdfVariant = 'desktop' | 'mobile';

// Report width (CSS px) and PDF page size (mm) for each variant
export const PDF_VARIANTS: Record<PdfVariant, { width: number; page: { w: number; h: number } }> = {
  desktop: { width: 1280, page: { w: 297, h: 210 } }, // A4 landscape
  mobile: { width: 420, page: { w: 120, h: 230 } }, // phone-shaped pages
};

// Tailwind/Bootstrap responsive classes follow the *browser window* width, not
// the report container, so the same page would be captured differently on
// different screens. While the PDF is generated this injects a stylesheet,
// scoped to `.pdf-capture`, that pins the chosen layout:
//  - desktop: every min-width media rule up to the report width is re-applied
//    unconditionally (desktop layout even from a narrow window / phone);
//  - mobile:  properties set by min-width rules are first reverted (browser
//    default), then every base (non-media) rule is re-applied on top, so the
//    element ends up exactly as on a phone even on a wide monitor.
// html2canvas clones the document including this <style>, so the capture
// matches. Returns a function that removes it.
export function pinPdfLayout(variant: PdfVariant): () => void {
  const reportWidth = PDF_VARIANTS[variant].width;
  const css: string[] = [];
  const resets: string[] = []; // mobile: undo desktop-only rules (must come before base rules)
  const scope = (selectorText: string) =>
    String(selectorText)
      .split(',')
      .map((sel) => `.pdf-capture ${sel.trim()}`)
      .join(', ');

  const collect = (rules: CSSRuleList) => {
    Array.from(rules).forEach((rule: any) => {
      if (variant === 'mobile') {
        if (rule.type === CSSRule.STYLE_RULE && !/^(html|body|:root)\b/.test(rule.selectorText)) {
          css.push(`${scope(rule.selectorText)} { ${rule.style.cssText} }`);
        }
        if (rule.type === CSSRule.MEDIA_RULE && /min-width/.test(rule.conditionText || rule.media?.mediaText || '')) {
          Array.from(rule.cssRules as CSSRuleList).forEach((inner: any) => {
            if (inner.type !== CSSRule.STYLE_RULE) return;
            const props = Array.from(inner.style as CSSStyleDeclaration).map((prop) => `${prop}: revert`);
            if (props.length) resets.push(`${scope(inner.selectorText)} { ${props.join('; ')} }`);
          });
        }
        return;
      }
      if (rule.type !== CSSRule.MEDIA_RULE) return;
      const cond: string = rule.conditionText || rule.media?.mediaText || '';
      const min = cond.match(/min-width:\s*(\d+(?:\.\d+)?)px/);
      if (!min || Number(min[1]) > reportWidth || /max-width|print|prefers|orientation|hover/.test(cond)) return;
      Array.from(rule.cssRules as CSSRuleList).forEach((inner: any) => {
        if (inner.type === CSSRule.STYLE_RULE) css.push(`${scope(inner.selectorText)} { ${inner.style.cssText} }`);
      });
    });
  };

  Array.from(document.styleSheets).forEach((sheet) => {
    try {
      collect(sheet.cssRules);
    } catch {
      // cross-origin stylesheet (e.g. Google Fonts) -- no layout rules we need
    }
  });

  const style = document.createElement('style');
  style.setAttribute('data-pdf-layout', variant);
  style.textContent = resets.concat(css).join('\n');
  document.head.appendChild(style);
  return () => style.remove();
}
